/**
 * Wire protocol shared by backend and frontend.
 *
 * Design rules:
 *  - Room-scoped events carry a monotonic `seq` per room, so a client can
 *    detect gaps. Connection-scoped frames (participants, rooms, error,
 *    pong) are not part of that sequence and carry `seq: 0`. A reconnecting client is
 *    re-sent the folded transcript in `hello` rather than a replayed gap:
 *    one code path, and it cannot drift from the server's view.
 *  - Live events are append-only facts. The client folds them into view
 *    state; the server never sends "replace the whole transcript" mid-stream.
 */

export const PROTOCOL_VERSION = 1;

/* ------------------------------------------------------------------ *
 * Identity
 * ------------------------------------------------------------------ */

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  picture?: string;
  /** Deterministic hue (0-359) derived from the user id, for avatar tinting. */
  hue: number;
}

export interface Participant extends UserProfile {
  /** Number of open sockets this user has. >1 means several tabs/devices. */
  connections: number;
  /** Epoch ms of the last keystroke in the composer; undefined = not typing. */
  typingSince?: number;
}

/* ------------------------------------------------------------------ *
 * Room
 * ------------------------------------------------------------------ */

/**
 * `idle`       - no turn running, accepting input
 * `queued`     - a prompt is waiting for the current turn to finish
 * `thinking`   - Claude is producing a turn
 * `compacting` - the CLI is compacting context
 * `awaiting`   - blocked on a permission decision from a human
 * `error`      - last turn failed; still accepts input
 */
export type RoomStatus =
  | 'idle'
  | 'queued'
  | 'thinking'
  | 'compacting'
  | 'awaiting'
  | 'error';

/**
 * How the agent authenticates, from the CLI's own `apiKeySource`.
 * `subscription` means a claude.ai login (the CLI reports 'none' — no API key
 * in use); `api-key` means metered API billing. Surfaced in the UI so nobody
 * has to guess which one is being spent.
 */
export type AuthSource = 'subscription' | 'api-key' | 'unknown';

export interface RoomSummary {
  id: string;
  name: string;
  /** Absolute path the agent runs in, shown read-only in the UI. */
  cwd: string;
  status: RoomStatus;
  model?: string;
  /** How hard Claude thinks. Sent even for a model without effort, which ignores it. */
  effort?: EffortLevel;
  permissionMode?: AgentPermissionMode;
  /** Claude Code session id, present once the first turn has started. */
  sessionId?: string;
  participantCount: number;
  lastActivityAt: number;
  totalCostUsd: number;
  authSource?: AuthSource;
}

/**
 * Who can open a room.
 *
 * `private`  - the owner, plus anyone in `sharedWith`.
 * `everyone` - every signed-in person, without being named.
 *
 * Note that `private` is not the opposite of shared: a room stays `private`
 * while being shared with three named people. The flag answers one question
 * only -- whether the room is open to the whole workspace -- and the member
 * list answers the rest.
 */
export type RoomVisibility = 'private' | 'everyone';

/**
 * A room as one particular person sees it.
 *
 * Access is per-viewer, so a room list is too: the same server state produces
 * a different array for each connection, and there is deliberately no frame
 * that carries "all rooms". `RoomSummary` is what the agent session knows
 * about itself; everything added here comes from the access store.
 */
export interface RoomView extends RoomSummary {
  /** Creator. Absent on rooms created from INITIAL_ROOMS, which nobody owns. */
  ownerEmail?: string;
  ownerName?: string;
  visibility: RoomVisibility;
  /** Individually invited people, owner excluded. */
  sharedWith: string[];
  /** The viewer created this room. */
  mine: boolean;
  /** The viewer may change sharing and the model. Today that means the same as `mine`. */
  canManage: boolean;
}

/**
 * What someone is typing in the composer right now, before they send it.
 *
 * Keyed by connection, not by person: the same person with two tabs open has
 * two composers, and one of them going quiet must not blank the other.
 */
export interface Draft {
  /** Connection id. */
  id: string;
  user: UserProfile;
  text: string;
}

/* ------------------------------------------------------------------ *
 * Room files
 * ------------------------------------------------------------------ */

/**
 * A file in a room's uploads folder.
 *
 * The folder is the source of truth, not a table: something the agent writes
 * there shows up too, just without an uploader.
 */
export interface RoomFile {
  name: string;
  /** Path relative to the room's working directory -- what Claude is told. */
  path: string;
  size: number;
  modifiedAt: number;
  uploadedBy?: { email: string; name: string };
}

/* ------------------------------------------------------------------ *
 * Artifacts
 * ------------------------------------------------------------------ */

/** Decides how the viewer renders an artifact. Derived from the extension. */
export type ArtifactKind = 'html' | 'markdown' | 'image' | 'pdf' | 'code' | 'text' | 'other';

export interface ArtifactVersion {
  version: number;
  size: number;
  createdAt: number;
  /** What changed, in Claude's words. */
  note?: string;
}

/**
 * Something Claude handed over on purpose, via its publish_artifact tool.
 *
 * Every publish snapshots the file, so later edits on disk never rewrite a
 * version someone already looked at. Publishing the same path again adds a
 * version to the same artifact instead of making a new one.
 */
export interface Artifact {
  id: string;
  title: string;
  /** The working file, relative to the room directory -- what to reference. */
  path: string;
  kind: ArtifactKind;
  createdAt: number;
  updatedAt: number;
  /** Oldest first; the last one is current. Never empty. */
  versions: ArtifactVersion[];
  /**
   * Whose artifact it is: the person whose turn first published it. Later
   * versions, whoever prompted them, do not change it. Artifacts from before
   * this was recorded belong to the room's owner, or to nobody in a room
   * nobody owns.
   */
  publishedBy?: { email: string; name: string };
}

/** One of your artifacts as the cross-room list shows it: where it lives. */
export interface OwnedArtifact extends Artifact {
  roomId: string;
  roomName: string;
}

/* ------------------------------------------------------------------ *
 * Transcript entries
 * ------------------------------------------------------------------ */

export interface ToolCall {
  id: string;
  name: string;
  input: unknown;
  /** Undefined while the tool is still running. */
  result?: { text: string; isError: boolean };
  /** Set for tool calls made by a subagent, so the UI can nest them. */
  parentToolUseId?: string | null;
  /** A personal-connection tool (Figma, Atlassian): whose account it ran with. */
  actingAs?: { email: string; name: string };
}

export type EntryKind =
  | 'prompt' // a human message
  | 'text' // Claude's prose
  | 'thinking' // Claude's reasoning summary
  | 'tool' // a tool call + its result
  | 'result' // end-of-turn summary (cost, duration)
  | 'notice'; // joins, leaves, interrupts, errors

export interface TranscriptEntry {
  id: string;
  kind: EntryKind;
  at: number;
  /** Present on `prompt` entries: who sent it. */
  author?: UserProfile;
  /** Present on `text` / `thinking`: the markdown body. */
  text?: string;
  /** Present on `tool`. */
  tool?: ToolCall;
  /** Present on `result`. */
  result?: TurnResult;
  /** Present on `notice`. */
  notice?: { level: 'info' | 'warn' | 'error'; message: string };
  /** True while the entry is still being streamed into. */
  streaming?: boolean;
  /** Set when a turn was cut short by a human pressing Stop. */
  aborted?: boolean;
}

export interface TurnResult {
  durationMs: number;
  numTurns: number;
  totalCostUsd: number;
  isError: boolean;
  stopReason?: string | null;
}

/* ------------------------------------------------------------------ *
 * Context window & slash commands
 * ------------------------------------------------------------------ */

/**
 * How full the room's context window is, from the CLI's own estimate
 * (`getContextUsage()`, the numbers behind /context). Refreshed after every turn and
 * every compaction; there is no live figure mid-turn.
 */
export interface ContextUsage {
  totalTokens: number;
  maxTokens: number;
  /** `deferred` rows are out-of-window tool schemas and count toward nothing. */
  categories: { name: string; tokens: number; kind: 'used' | 'free' | 'buffer' | 'deferred' }[];
  at: number;
}

/** A command the composer can offer after "/". Sent to the CLI verbatim. */
export interface SlashCommandInfo {
  name: string;
  description: string;
  argumentHint?: string;
}

/* ------------------------------------------------------------------ *
 * Permission requests
 * ------------------------------------------------------------------ */

export interface PermissionRequest {
  id: string;
  toolName: string;
  input: unknown;
  at: number;
  /** Server-side deadline; the UI counts down to it. */
  expiresAt: number;
  /**
   * Set when the tool would use someone's personal account (Figma,
   * Atlassian). Only that person may allow it; anyone may deny.
   */
  actingAs?: { email: string; name: string };
}

export interface PermissionOutcome {
  id: string;
  decision: 'allow' | 'deny' | 'timeout';
  decidedBy?: UserProfile;
  reason?: string;
}

/* ------------------------------------------------------------------ *
 * Server -> client
 * ------------------------------------------------------------------ */

export type ServerEvent =
  /** First frame after a socket joins a room. Never replayed. */
  | {
      t: 'hello';
      seq: number;
      protocol: number;
      me: UserProfile;
      room: RoomView;
      rooms: RoomView[];
      participants: Participant[];
      /** Backlog. Empty when the client resumed and missed nothing. */
      entries: TranscriptEntry[];
      /** True when the backlog was trimmed because the client fell too far behind. */
      truncated: boolean;
      pendingPermission?: PermissionRequest;
      files: RoomFile[];
      artifacts: Artifact[];
      /** Messages other people in the room are in the middle of writing. */
      drafts: Draft[];
      /** Models a room owner may pick from; the server default first. */
      models: string[];
      /** Absent until the room's session has finished a turn. */
      context?: ContextUsage;
      commands: SlashCommandInfo[];
    }
  | { t: 'entry'; seq: number; entry: TranscriptEntry }
  /** Incremental text appended to an existing streaming entry. */
  | { t: 'delta'; seq: number; entryId: string; text: string }
  /** Marks a streaming entry complete, optionally replacing its final text. */
  | { t: 'entry-done'; seq: number; entryId: string; text?: string }
  /** A tool call's result arrived. */
  | { t: 'tool-result'; seq: number; entryId: string; result: ToolCall['result'] }
  | {
      t: 'status';
      seq: number;
      status: RoomStatus;
      model?: string;
      effort?: EffortLevel;
      permissionMode?: AgentPermissionMode;
      sessionId?: string;
      authSource?: AuthSource;
    }
  | { t: 'participants'; seq: number; participants: Participant[] }
  /** A draft changed. Empty `text` means it was sent, cleared or abandoned. */
  | { t: 'draft'; seq: number; draft: Draft }
  | { t: 'rooms'; seq: number; rooms: RoomView[] }
  | { t: 'queue'; seq: number; depth: number }
  | { t: 'permission-request'; seq: number; request: PermissionRequest }
  | { t: 'permission-resolved'; seq: number; outcome: PermissionOutcome }
  | { t: 'cost'; seq: number; totalCostUsd: number }
  | { t: 'context'; seq: number; context: ContextUsage }
  | { t: 'commands'; seq: number; commands: SlashCommandInfo[] }
  /** The room's file list changed. Carries the whole list; it is short. */
  | { t: 'files'; seq: number; files: RoomFile[] }
  /** The room's artifact list changed. Newest first, whole list. */
  | { t: 'artifacts'; seq: number; artifacts: Artifact[] }
  | { t: 'error'; seq: number; message: string; fatal?: boolean }
  | { t: 'pong'; seq: number };

/* ------------------------------------------------------------------ *
 * Client -> server
 * ------------------------------------------------------------------ */

/**
 * `ServerEvent` minus the `seq` the room stamps on emit. Written distributively
 * so each union member keeps its own payload keys — a plain `Omit` over a union
 * collapses it to the keys they share.
 */
export type ServerEventInput = ServerEvent extends infer E
  ? E extends { seq: number }
    ? Omit<E, 'seq'>
    : never
  : never;

export type ClientCommand =
  /** Text starting with "/" is a slash command and reaches the CLI as typed. */
  | { t: 'prompt'; text: string }
  | { t: 'interrupt' }
  | { t: 'typing'; active: boolean }
  /** The composer's current text, throttled by the client. Empty clears it. */
  | { t: 'draft'; text: string }
  | { t: 'permission-decide'; id: string; decision: 'allow' | 'deny'; reason?: string }
  | { t: 'set-permission-mode'; mode: AgentPermissionMode }
  /** Owner only. `model` must be one of the `models` sent in `hello`. */
  | { t: 'set-model'; model: string }
  /** Owner only, like the model. */
  | { t: 'set-effort'; effort: EffortLevel }
  | { t: 'switch-room'; roomId: string }
  /** The server picks the directory: rooms/<owner>/<room id> under the workspace. */
  | { t: 'create-room'; name: string }
  /** Owner only. Deletes the room's directory and Claude session with it. */
  | { t: 'delete-room'; roomId: string }
  /** Owner only. `email` must be someone the access policy would let sign in. */
  | { t: 'share-room'; roomId: string; email: string }
  | { t: 'unshare-room'; roomId: string; email: string }
  | { t: 'set-room-visibility'; roomId: string; visibility: RoomVisibility }
  | { t: 'ping' };

/**
 * Mirrors the SDK's PermissionMode, minus modes we do not expose.
 *
 * `default` asks before anything that is not read-only; `auto` lets Claude
 * Code's own classifier approve routine actions and block risky ones,
 * without asking; `plan` only plans. `acceptEdits` is still accepted from
 * older clients but no longer offered.
 */
export type AgentPermissionMode = 'default' | 'acceptEdits' | 'auto' | 'plan';

export const PERMISSION_MODES = ['default', 'acceptEdits', 'auto', 'plan'] as const;

export function isPermissionMode(value: unknown): value is AgentPermissionMode {
  return (PERMISSION_MODES as readonly unknown[]).includes(value);
}

/** The SDK's EffortLevel, lowest first -- the order the picker lays them out in. */
export const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type EffortLevel = (typeof EFFORT_LEVELS)[number];
export const DEFAULT_EFFORT: EffortLevel = 'high';

export function isEffortLevel(value: unknown): value is EffortLevel {
  return (EFFORT_LEVELS as readonly unknown[]).includes(value);
}

/** Haiku takes no effort parameter; the CLI would silently drop it. */
export function supportsEffort(model: string): boolean {
  return !/haiku/i.test(model);
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

/** Stable hue from a user id so avatars keep the same colour everywhere. */
export function hueFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
}

/** Tools that never mutate anything and can be auto-approved. */
export const READ_ONLY_TOOLS = [
  'Read',
  'Glob',
  'Grep',
  'WebFetch',
  'WebSearch',
  'NotebookRead',
  'TodoWrite',
  'Task',
] as const;

/**
 * The tool Claude calls to hand something over, as the SDK names it
 * (`mcp__<server>__<tool>`). The transcript draws these calls as artifact
 * cards rather than as generic tool rows.
 */
export const PUBLISH_ARTIFACT_TOOL = 'mcp__cowork__publish_artifact';
